import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SolutionsTiers = () => {
  const [selectedTier, setSelectedTier] = useState('academic');

  const tiers = [
    {
      id: 'academic',
      name: 'Academic Partnerships',
      icon: 'GraduationCap',
      target: 'Universities & Colleges',
      description: 'Comprehensive AI/Robotics curriculum integration for higher education institutions',
      features: [
        'Custom curriculum development',
        'Faculty training programs',
        'Student project mentorship',
        'Research collaboration opportunities',
        'Industry placement support',
        'Certification programs'
      ],
      metrics: {
        implementation: '6-8 weeks',
        support: '24/7 academic support',
        pricing: 'Volume-based pricing',
        success: '98% student satisfaction'
      },
      caseStudy: {
        institution: 'Stanford University',
        result: '40% increase in AI course enrollment',
        quote: "Dream Coders Club transformed our computer science program with cutting-edge AI curriculum."
      }
    },
    {
      id: 'corporate',
      name: 'Corporate Training',
      icon: 'Building2',
      target: 'Enterprise Organizations',
      description: 'Workforce transformation through AI and automation training programs',
      features: [
        'Executive leadership training',
        'Technical skill development',
        'Change management support',
        'ROI tracking and analytics',
        'Custom learning paths',
        'Integration with existing systems'
      ],
      metrics: {
        implementation: '4-6 weeks',
        support: 'Dedicated account manager',
        pricing: 'Enterprise licensing',
        success: '85% productivity increase'
      },
      caseStudy: {
        institution: 'Microsoft Corporation',
        result: '60% faster AI project delivery',
        quote: "Our teams are now equipped with the AI skills needed for the future of work."
      }
    },
    {
      id: 'government',
      name: 'Government Programs',
      icon: 'Shield',
      target: 'Public Sector Organizations',
      description: 'Specialized training programs for government agencies and public institutions',
      features: [
        'Security-compliant platforms',
        'Public sector pricing',
        'Compliance training modules',
        'Multi-agency coordination',
        'Citizen service improvement',
        'Digital transformation support'
      ],
      metrics: {
        implementation: '8-12 weeks',
        support: 'Government liaison team',
        pricing: 'Public sector rates',
        success: '92% compliance achievement'
      },
      caseStudy: {
        institution: 'Department of Defense',
        result: '50% improvement in operational efficiency',
        quote: "Critical AI capabilities delivered with the highest security standards."
      }
    }
  ];

  const currentTier = tiers.find(tier => tier.id === selectedTier);

  return (
    <section className="py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-foreground mb-4">
            Solutions for Every
            <span className="gradient-text"> Organization</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Tailored AI and Robotics education solutions designed for your specific organizational needs and goals
          </p>
        </div>

        {/* Tier Selection */}
        <div className="flex flex-col lg:flex-row gap-4 mb-12 justify-center">
          {tiers.map((tier) => (
            <button
              key={tier.id}
              onClick={() => setSelectedTier(tier.id)}
              className={`p-6 rounded-xl border-2 transition-all duration-300 text-left ${
                selectedTier === tier.id
                  ? 'border-accent bg-accent/10 shadow-glow'
                  : 'border-border bg-card hover:border-accent/50 hover:bg-accent/5'
              }`}
            >
              <div className="flex items-center space-x-4">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                  selectedTier === tier.id
                    ? 'bg-accent text-white' :'bg-muted text-muted-foreground'
                }`}>
                  <Icon name={tier.icon} size={24} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-foreground">{tier.name}</h3>
                  <p className="text-sm text-muted-foreground">{tier.target}</p>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Selected Tier Details */}
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left Column - Features & Metrics */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-bold text-foreground mb-4">{currentTier.name}</h3>
              <p className="text-muted-foreground text-lg mb-6">{currentTier.description}</p>
            </div>

            {/* Features */}
            <div>
              <h4 className="text-lg font-semibold text-foreground mb-4">Key Features</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {currentTier.features.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <Icon name="CheckCircle" size={16} className="text-success flex-shrink-0" />
                    <span className="text-muted-foreground">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Metrics */}
            <div>
              <h4 className="text-lg font-semibold text-foreground mb-4">Implementation Details</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-card rounded-lg border border-border">
                  <div className="text-sm text-muted-foreground">Implementation</div>
                  <div className="text-lg font-semibold text-foreground">{currentTier.metrics.implementation}</div>
                </div>
                <div className="p-4 bg-card rounded-lg border border-border">
                  <div className="text-sm text-muted-foreground">Support</div>
                  <div className="text-lg font-semibold text-foreground">{currentTier.metrics.support}</div>
                </div>
                <div className="p-4 bg-card rounded-lg border border-border">
                  <div className="text-sm text-muted-foreground">Pricing</div>
                  <div className="text-lg font-semibold text-foreground">{currentTier.metrics.pricing}</div>
                </div>
                <div className="p-4 bg-card rounded-lg border border-border">
                  <div className="text-sm text-muted-foreground">Success Rate</div>
                  <div className="text-lg font-semibold text-success">{currentTier.metrics.success}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Case Study */}
          <div className="space-y-8">
            <div className="holographic-card p-8 bg-card">
              <div className="space-y-6">
                <div className="flex items-center space-x-3">
                  <Icon name="Star" size={20} className="text-warning" />
                  <span className="text-sm font-medium text-warning">Success Story</span>
                </div>
                
                <div>
                  <h4 className="text-xl font-bold text-foreground mb-2">{currentTier.caseStudy.institution}</h4>
                  <div className="text-2xl font-bold text-accent mb-4">{currentTier.caseStudy.result}</div>
                  <blockquote className="text-muted-foreground italic border-l-4 border-accent pl-4">
                    "{currentTier.caseStudy.quote}"
                  </blockquote>
                </div>

                <div className="flex items-center space-x-4 pt-4 border-t border-border">
                  <div className="w-12 h-12 bg-gradient-to-br from-accent to-cyber-purple rounded-full flex items-center justify-center">
                    <Icon name="User" size={20} color="white" />
                  </div>
                  <div>
                    <div className="font-semibold text-foreground">Dr. Sarah Johnson</div>
                    <div className="text-sm text-muted-foreground">Chief Technology Officer</div>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="space-y-4">
              <Button
                variant="default"
                size="lg"
                fullWidth
                className="morphing-cta text-white font-semibold"
                iconName="Calendar"
                iconPosition="left"
              >
                Schedule Consultation
              </Button>
              
              <Button
                variant="outline"
                size="lg"
                fullWidth
                iconName="FileText"
                iconPosition="left"
              >
                Download Case Study
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SolutionsTiers;